import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import PaneDetailsSection from 'components/Common/Pane/PaneDetailsSection'
import List from 'components/Common/List'
import { mediaQueryUp } from 'components/utils/breakpoint'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import { ImageParams } from 'components/Common/ResponsiveImage'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import useToggle from 'hooks/useToggle'
import { EmptyArray, sortBy } from 'lib/array/arrayUtils'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import OfferPageRoomGallery from '../OfferPageRoomGallery'
import { isLoadingByRoom } from 'selectors/multipleRoomBookingSelectors'
import MultipleRoomsBookingModalOption from './MultipleRoomsBookingModalOption'

const Container = styled(Pane)`
  position: relative;
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const ContentGrid = styled.div`
  display: grid;
  grid-gap: ${rem(16)};
  grid-template-columns: 1fr;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
    grid-gap: ${rem(24)};
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: ${rem(300)} 1fr;
  }
`

const TitleSection = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;

  > * + * {
    margin-top: ${rem(12)};
  }

  ${mediaQueryUp.tablet} {
    grid-column: 1 / span 3;
  }

  ${mediaQueryUp.desktop} {
    text-align: left;
    grid-column: 1;

    > * + * {
      margin-top: 0;
    }
  }
`

const DescriptionSection = styled.div`
  display: flex;
  flex-direction: column;

  grid-column: 1;
  grid-row: 2;
  padding: ${rem(16)} ${rem(24)};

  > * + * {
    margin-top: ${rem(8)};
  }

  ${mediaQueryUp.tablet} {
    grid-column: 1 / span 3;
    padding: ${rem(16)} ${rem(24)};
  }

  ${mediaQueryUp.desktop} {
    grid-column: 2;
    grid-row: 1;
    padding: ${rem(12)} 0;
    padding-right: ${rem(4)};
  }
`

const RoomImages = styled(OfferPageRoomGallery)`
  width: 100%;
`

const Description = styled.div`
  > * + * {
    margin-top: ${rem(8)};

    ${mediaQueryUp.desktop} {
      margin-top: ${rem(12)};
    }
  }
`

const DetailsPane = styled.div`
  padding: ${rem(16)};

  > * + * {
    margin-top: ${rem(16)};
  }

  ${mediaQueryUp.tablet} {
    display: flex;

    > * {
      margin-top: 0;
      flex: 1 1 50%;

      &:nth-child(2) {
        /* First child is the close cross */
        padding-right: ${rem(30)};
      }

      &:last-child {
        padding-left: ${rem(30)};
      }
    }
  }
`

const DetailsDescription = styled(MarkdownRender)`
  margin-top: ${rem(12)};
`

const AmenityList = styled(List)`
  margin-top: ${rem(12)};
  margin-bottom: 0;

  ${mediaQueryUp.tablet} {
    columns: 2;
  }
`

const emptyPackageOptions: Array<OfferPackageOptionView> = EmptyArray

interface Props {
  offer: App.Offer;
  packageViewOptions: Array<OfferPackageOptionView>;
  fetching: boolean;
  selectedPackageUniqueKey: string;
  room: App.RoomOccupants;
  onSelect: (packageOption: App.Package) => void;
}

const imageProps: ImageParams = {
  mobileAspectRatio: '32:13',
  tabletAspectRatio: '35:12',
  desktopAspectRatio: '3:2',
  desktopWidth: '420px',
  quality: 'good',
}

function MultipleRoomsBookingModalRoomDetails(props: Props) {
  const {
    offer,
    packageViewOptions = emptyPackageOptions,
    fetching,
    room,
    selectedPackageUniqueKey,
    onSelect,
  } = props

  const roomType = packageViewOptions[0]?.package.roomType

  const [showDetails, onToggleDetails] = useToggle()

  const sortedAmenities = useMemo(() => sortBy(roomType.amenities, a => a.name, 'asc'), [roomType])

  return (
    <Container>
      <ScrollAnchor id={roomType.id} vOffset={rem(80)} />
      <ContentGrid>
        <TitleSection>
          {roomType.images.length > 0 && <RoomImages
            room={roomType}
            imageProps={imageProps}
          />}
        </TitleSection>
        <DescriptionSection>
          <Description>
            {roomType.name &&
              <Heading variant="heading5">{roomType.name}</Heading>
            }
            {roomType.sizeSqm &&
              <BodyText variant="medium">
                <strong>Size:</strong> {roomType.sizeSqm} m&sup2;
              </BodyText>
            }
            {roomType.description && <BodyText variant="medium">{roomType.description}</BodyText>}
          </Description>
          <SmallToggleButton size="small" onClick={onToggleDetails} on={showDetails}>
            {showDetails ? 'Hide' : 'View'} room details
          </SmallToggleButton>
        </DescriptionSection>
      </ContentGrid>

      <PaneDetailsSection isOpen={showDetails} onToggle={onToggleDetails}>
        <DetailsPane>
          <div>
            <BodyText variant="large" weight="bold">Room facilities:</BodyText>
            <BodyText variant="medium" >
              <AmenityList>
                {sortedAmenities.map(amenity =>
                  <li key={amenity.id}>{amenity.name}</li>,
                )}
              </AmenityList>
            </BodyText>
          </div>
          <div>
            {packageViewOptions[0]?.package.roomPolicyDescription && <>
              <BodyText variant="medium" weight="bold">Room policies:</BodyText>
              <DetailsDescription fontSize="medium" content={packageViewOptions[0].package.roomPolicyDescription} type="compact" />
            </>}
          </div>
        </DetailsPane>
      </PaneDetailsSection>

      {packageViewOptions.map(pkgView => (
        <MultipleRoomsBookingModalOption
          key={pkgView.package.uniqueKey}
          packageView={pkgView}
          offer={offer}
          room={room}
          selectedPackageUniqueKey={selectedPackageUniqueKey}
          onSelect={onSelect}
        />
      ))}

      <LoadingIndicator visible={fetching} floating opaque />
    </Container>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps: Props) => ({
  checkIn: state.checkInDate,
  duration: state.duration,
  fetching: isLoadingByRoom(state, appState)[ownProps.room.roomId],
}))(MultipleRoomsBookingModalRoomDetails)
